import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { 
  UserPlus, Search, Edit3, Trash2, X, 
  ShieldCheck, User as UserIcon, Filter,
  CheckCircle2, AlertCircle, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [facilities, setFacilities] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState(null);
  
  // State quản lý Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    username: '', 
    password: '', 
    fullname: '', 
    role: 'user', 
    facility_id: '' 
  });

  const showToast = (message, isError = false) => {
    setToastMessage({ text: message, isError });
    setTimeout(() => setToastMessage(null), 3500);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      const [userRes, facRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/facilities`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      // Lấy data từ response (hỗ trợ cả 2 chuẩn trả về phổ biến)
      const userData = userRes.data.data || userRes.data;
      const facData = facRes.data.data || facRes.data;
      
      setUsers(userData);
      setFacilities(facData);
      
      // Chọn mặc định cơ sở đầu tiên nếu có
      if (facData.length > 0) {
        setFormData(prev => ({ ...prev, facility_id: facData[0].id }));
      }
    } catch (err) {
      console.error("Lỗi fetch data:", err);
      showToast("Không thể tải dữ liệu. Vui lòng kiểm tra kết nối Server.", true);
    } finally {
      setLoading(false);
    }
  };

  // Chỉ gọi hàm fetchData() ở đây
  useEffect(() => {
    fetchData();
  }, []);

  // Mở modal Thêm
  const handleAddNew = () => {
    setEditingItem(null);
    setFormData({ 
      username: '', 
      password: '', 
      fullname: '', 
      role: 'user', 
      facility_id: facilities.length > 0 ? facilities[0].id : '' 
    });
    setIsModalOpen(true);
  };

  // Mở modal Sửa
  const handleEdit = (user) => {
    setEditingItem(user);
    setFormData({
      username: user.username,
      password: '', // Pass rỗng để ngầm hiểu là không đổi pass
      fullname: user.fullname,
      role: user.role,
      facility_id: user.facility_id
    });
    setIsModalOpen(true);
  };

  // Xử lý Xóa
  const handleDelete = async (id) => {
    if(!window.confirm("Bạn có chắc chắn muốn xóa thành viên này? Thao tác không thể phục hồi!")) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API_URL}/admin/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsers(users.filter(u => u.id !== id));
      showToast("Đã xóa người dùng thành công!");
    } catch (err) {
      console.error("Lỗi xóa:", err);
      showToast(err.response?.data?.detail || "Có lỗi xảy ra khi xóa.", true);
    }
  };

  // Xử lý Lưu (Thêm mới / Cập nhật)
  const handleSaveUser = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      const token = localStorage.getItem('admin_token');
      const dataToSend = {
        ...formData,
        facility_id: parseInt(formData.facility_id) 
      };
      
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingItem) {
        // Cập nhật User
        await axios.put(`${API_URL}/admin/users/${editingItem.id}`, dataToSend, config);
        showToast("Đã cập nhật thông tin thành công!");
      } else {
        // Thêm User mới
        await axios.post(`${API_URL}/admin/users`, dataToSend, config);
        showToast("Đã thêm thành viên mới thành công!");
      }

      setIsModalOpen(false); 
      fetchData(); 
    } catch (err) {
      console.error("Lỗi lưu:", err.response?.data);
      const errorDetail = err.response?.data?.detail;
      showToast(typeof errorDetail === 'string' ? errorDetail : "Lỗi dữ liệu đầu vào. Vui lòng kiểm tra lại.", true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full w-full gap-3 lg:gap-4 relative font-sans text-slate-600">
      
      {/* ================= TOAST THÔNG BÁO (PORTAL) ================= */}
      {toastMessage && createPortal(
        <div className={`fixed bottom-10 right-1/2 translate-x-1/2 lg:translate-x-0 lg:right-8 z-[9999] text-white px-5 py-3.5 rounded-2xl shadow-2xl flex items-center gap-3 animate-slide-up-fade whitespace-nowrap ${toastMessage.isError ? 'bg-rose-600' : 'bg-slate-800'}`}>
          {toastMessage.isError ? <AlertCircle size={18} className="text-rose-200" /> : <CheckCircle2 size={18} className="text-emerald-400" />}
          <span className="text-sm font-bold">{toastMessage.text}</span>
        </div>,
        document.body
      )}

      {/* 1. THANH CÔNG CỤ (ACTION BAR) */}
      <div className="shrink-0 bg-white/70 backdrop-blur-xl border border-white rounded-2xl px-3 lg:px-5 py-3 flex flex-col lg:flex-row lg:items-center justify-between gap-3 shadow-sm z-20">
        <div className="relative group flex-1 lg:max-w-md">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-sky-500 transition-colors" />
          <input 
            type="text" 
            placeholder="Tìm theo tên, tài khoản..." 
            className="w-full pl-9 pr-3 py-2 bg-white/80 border border-slate-200/60 rounded-xl text-xs outline-none focus:bg-white focus:border-sky-300 focus:ring-2 focus:ring-sky-50 transition-all font-medium shadow-sm"
          />
        </div>
        
        <div className="flex items-center gap-2 lg:gap-3 w-full lg:w-auto">
          <button className="p-2 lg:px-3 lg:py-2 flex items-center justify-center bg-white/80 text-slate-500 border border-slate-200/60 rounded-xl hover:bg-sky-50 hover:text-sky-600 transition-colors shadow-sm shrink-0">
            <Filter size={16} />
          </button>
          
          <button 
            onClick={handleAddNew}
            className="flex-1 lg:flex-none flex items-center justify-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-xl text-xs font-bold tracking-wide hover:bg-sky-600 transition-colors cursor-pointer shadow-sm shrink-0 active:scale-95"
          >
            <UserPlus size={14} /> 
            <span className="hidden sm:inline">Thêm mới</span>
            <span className="sm:hidden">Thêm</span>
          </button>
        </div>
      </div>

      {/* 2. VÙNG DỮ LIỆU CHÍNH */}
      <main className="flex-1 bg-white/70 backdrop-blur-xl rounded-2xl border border-white shadow-sm flex flex-col overflow-hidden relative">
        <div className="flex-1 w-full overflow-auto custom-scrollbar">
          
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full py-20 text-slate-400">
              <div className="w-8 h-8 border-4 border-sky-200 border-t-sky-500 rounded-full animate-spin mb-4"></div>
              <p className="font-medium text-sm italic">Đang truy xuất dữ liệu...</p>
            </div>
          ) : (
            <>
              {/* --- DESKTOP VIEW --- */}
              <div className="hidden md:block min-w-max">
                <table className="w-full text-left border-collapse">
                  <thead className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur-md">
                    <tr className="text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-slate-200/60">
                      <th className="px-5 py-4 whitespace-nowrap">Thành viên</th>
                      <th className="px-5 py-4 whitespace-nowrap">Cơ sở y tế</th>
                      <th className="px-5 py-4 whitespace-nowrap text-center">Vai trò</th>
                      <th className="px-5 py-4 text-center sticky right-0 bg-slate-50/90 backdrop-blur-md z-10 border-l border-slate-200/60 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.05)]">Xử lý</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100/60 text-sm">
                    {users.length > 0 ? users.map((user) => (
                      <tr key={user.id} className="hover:bg-sky-50/50 transition-colors group">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-white shadow-sm ${user.role?.toLowerCase() === 'admin' ? 'bg-indigo-500' : 'bg-sky-500'}`}>
                              {(user.fullname || user.username).charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-bold text-slate-700 leading-tight">{user.fullname || 'Hội viên mới'}</p>
                              <p className="text-[11px] font-mono text-slate-400 mt-0.5">@{user.username}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-xs font-semibold text-slate-600">
                           {facilities.find(f => f.id === user.facility_id)?.ten_cskcb || `Cơ sở ID: ${user.facility_id}`}
                        </td>
                        <td className="px-5 py-3 text-center">
                          <span className={`inline-flex px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider items-center gap-1 border ${
                            user.role?.toLowerCase() === 'admin' ? 'bg-indigo-50 text-indigo-600 border-indigo-100' : 'bg-sky-50 text-sky-600 border-sky-100'
                          }`}>
                            {user.role?.toLowerCase() === 'admin' ? <ShieldCheck size={10} strokeWidth={2.5}/> : <UserIcon size={10} strokeWidth={2.5}/>}
                            {user.role}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-center sticky right-0 bg-white/50 backdrop-blur-sm group-hover:bg-sky-50/80 border-l border-slate-100/60 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.02)] transition-colors">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => handleEdit(user)} className="p-1.5 text-slate-400 hover:text-sky-600 hover:bg-white rounded-lg transition-all border border-transparent hover:border-sky-200 shadow-sm">
                              <Edit3 size={15} />
                            </button>
                            <button onClick={() => handleDelete(user.id)} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-white rounded-lg transition-all border border-transparent hover:border-rose-200 shadow-sm">
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="4" className="px-5 py-10 text-center text-slate-400 font-medium">Chưa có người dùng nào.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* --- MOBILE VIEW --- */}
              <div className="md:hidden p-3 space-y-3 pb-[calc(1rem+env(safe-area-inset-bottom))]">
                {users.length > 0 ? users.map((user) => (
                  <div key={user.id} className="bg-white/80 backdrop-blur-sm p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-col gap-3 relative overflow-hidden group">
                    <div className={`absolute left-0 top-0 bottom-0 w-1 ${user.role?.toLowerCase() === 'admin' ? 'bg-indigo-400' : 'bg-sky-300'}`}></div>
                    
                    <div className="flex justify-between items-start ml-1">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white shadow-sm shrink-0 ${user.role?.toLowerCase() === 'admin' ? 'bg-indigo-500' : 'bg-sky-500'}`}>
                          {(user.fullname || user.username).charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-800 text-[13px] leading-snug">{user.fullname || 'Hội viên mới'}</h4>
                          <p className="text-[11px] font-mono text-slate-500 mt-0.5">@{user.username}</p>
                        </div>
                      </div>
                      
                      <span className={`inline-flex px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider items-center gap-1 border ${
                        user.role?.toLowerCase() === 'admin' ? 'bg-indigo-50 text-indigo-600 border-indigo-100' : 'bg-sky-50 text-sky-600 border-sky-100'
                      }`}>
                        {user.role}
                      </span>
                    </div>

                    <div className="ml-1 pl-12 flex justify-between items-end border-t border-slate-100/60 pt-2">
                       <div className="text-[11px] font-semibold text-slate-500 line-clamp-1">
                          {facilities.find(f => f.id === user.facility_id)?.ten_cskcb || `Cơ sở ID: ${user.facility_id}`}
                       </div>
                       <div className="flex gap-2 shrink-0">
                         <button onClick={() => handleEdit(user)} className="text-slate-400 hover:text-sky-600 p-1"><Edit3 size={15} /></button>
                         <button onClick={() => handleDelete(user.id)} className="text-slate-400 hover:text-rose-500 p-1"><Trash2 size={15} /></button>
                       </div>
                    </div>
                  </div>
                )) : (
                  <div className="py-10 text-center text-slate-400 font-medium text-sm">Chưa có người dùng nào.</div>
                )}
              </div>
            </>
          )}
        </div>
      </main>

      {/* 3. MODAL THÊM / SỬA USER (PORTAL LÊN BODY Z-INDEX LỚN NHẤT) */}
      {isModalOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm animate-fade-in" onClick={() => setIsModalOpen(false)}></div>
          
          <div className="bg-white/90 backdrop-blur-2xl w-full max-w-md rounded-[2rem] shadow-2xl overflow-hidden animate-slide-up-fade border border-white relative z-10 flex flex-col max-h-[90vh]">
            
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-sky-100 text-sky-600 rounded-lg shadow-inner">
                  {editingItem ? <Edit3 size={16} strokeWidth={2.5}/> : <UserPlus size={16} strokeWidth={2.5} />}
                </div>
                <h3 className="text-sm font-black text-slate-800 tracking-tight uppercase">
                  {editingItem ? 'Cập Nhật Thành Viên' : 'Thêm Thành Viên'}
                </h3>
              </div>
              <button disabled={isSaving} onClick={() => setIsModalOpen(false)} className="p-1.5 text-slate-400 hover:bg-white hover:text-rose-500 rounded-lg transition-colors disabled:opacity-50">
                <X size={18} strokeWidth={2.5}/>
              </button>
            </div>

            <form onSubmit={handleSaveUser} className="p-5 lg:p-6 space-y-4 overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-2 gap-3 lg:gap-4">
                <ThinInput 
                  label="Tài khoản *" 
                  required={true}
                  value={formData.username} 
                  onChange={(e) => setFormData({...formData, username: e.target.value})} 
                />
                <ThinInput 
                  label={editingItem ? "Mật khẩu mới" : "Mật khẩu *"} 
                  type="password"
                  required={!editingItem} 
                  placeholder={editingItem ? "Để trống nếu không đổi" : "Nhập mật khẩu"}
                  value={formData.password} 
                  onChange={(e) => setFormData({...formData, password: e.target.value})} 
                />
              </div>

              <ThinInput 
                label="Họ tên nhân viên *" 
                required={true}
                value={formData.fullname} 
                onChange={(e) => setFormData({...formData, fullname: e.target.value})} 
              />

              <div className="space-y-1.5">
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Vai trò</label>
                <select 
                  className="w-full px-3.5 py-2.5 bg-slate-50/80 border border-slate-200/80 rounded-xl text-xs outline-none focus:bg-white focus:border-sky-300 focus:ring-2 focus:ring-sky-50 transition-all font-semibold text-slate-700 cursor-pointer appearance-none"
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                >
                  <option value="user">User (Nhân viên)</option>
                  <option value="admin">Admin (Quản trị hệ thống)</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Cơ sở y tế trực thuộc</label>
                <select 
                  required
                  className="w-full px-3.5 py-2.5 bg-slate-50/80 border border-slate-200/80 rounded-xl text-xs outline-none focus:bg-white focus:border-sky-300 focus:ring-2 focus:ring-sky-50 transition-all font-semibold text-slate-700 cursor-pointer appearance-none line-clamp-1"
                  value={formData.facility_id}
                  onChange={(e) => setFormData({...formData, facility_id: e.target.value})}
                >
                  {facilities.map((fac) => (
                    <option key={fac.id} value={fac.id}>
                      {fac.ma_cskcb} - {fac.ten_cskcb}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pt-2">
                <button 
                  type="submit"
                  disabled={isSaving}
                  className="w-full bg-sky-600 hover:bg-sky-700 disabled:bg-sky-400 text-white py-3.5 rounded-xl font-bold shadow-md shadow-sky-600/20 transition-all active:scale-[0.98] uppercase tracking-widest text-xs flex items-center justify-center gap-2"
                >
                  {isSaving ? <Loader2 size={14} className="animate-spin" /> : (editingItem ? <Edit3 size={14} /> : <UserPlus size={14} />)}
                  {isSaving ? 'ĐANG XỬ LÝ...' : (editingItem ? 'LƯU THAY ĐỔI' : 'TẠO TÀI KHOẢN')}
                </button>
              </div>
            </form>

          </div>
        </div>,
        document.body
      )}

      {/* --- CSS Animations --- */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 8px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fadeIn 0.2s ease-out forwards; }
        
        @keyframes slideUpFade {
          from { opacity: 0; transform: translateY(20px) translateX(-50%) scale(0.95); }
          to { opacity: 1; transform: translateY(0) translateX(-50%) scale(1); }
        }
        @media (min-width: 1024px) {
          @keyframes slideUpFade {
            from { opacity: 0; transform: translateY(20px) scale(0.95); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
        }
        .animate-slide-up-fade { animation: slideUpFade 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
      `}</style>
    </div>
  );
};

// Component con cho Input
const ThinInput = ({ label, value, onChange, type="text", required=false, placeholder="" }) => (
  <div className="space-y-1.5">
    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">{label}</label>
    <input 
      required={required}
      type={type} 
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      className="w-full px-3.5 py-2.5 bg-slate-50/80 border border-slate-200/80 rounded-xl text-xs outline-none focus:bg-white focus:border-sky-300 focus:ring-2 focus:ring-sky-50 transition-all font-semibold text-slate-700 placeholder:font-normal placeholder:text-slate-400"
    />
  </div>
);

export default UserManagement;